import { Injectable } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { BehaviorSubject } from "rxjs";

import { APIURLTypes } from "../enums/apiurltypes.enum";
import { APIUrlModel } from "../model/apiurl.model";
import {
  AnalyticFilterModel,
  AnalyticsFilter,
  FilterParamsApiModel,
} from "@dashboard/model/analyticFilterModel/analytic-filter-model";
import { SubscriptionBasedOnCategory } from "../model/commodity.model";
import {
  DeviceDataChart,
  ScanApiModel,
} from "../model/installation-centers-deatils.model";
import { environment } from "src/environments/environment";
import { remove } from "@amcharts/amcharts4/.internal/core/utils/Array";

@Injectable({
  providedIn: "root",
})
export class HelperService {
  constructor() {
    // localStorage is shared by all tabs: when another tab switches organization (or logs out), reload this
    // tab so it never shows one organization while its requests go to another.
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === 'activeCustomerId' && event.oldValue !== event.newValue) {
        window.location.reload();
      }
    });
  }
  public pageSize = 10;
  public skipPages = 0;
  public showPagingLimit = 9;
  public listTotalRecords = 0;
  private isAdmin = false;
  private commoditySelected = new BehaviorSubject<
    SubscriptionBasedOnCategory[]
  >([]);
  public commodityValue = this.commoditySelected.asObservable();
  private commodityValueBasedOnDropDown = new BehaviorSubject<number>(null);
  public commodityValueBasedOnDropDownValue =
    this.commodityValueBasedOnDropDown.asObservable();
  private commodityCategoryValueBasedOnDropDown = new BehaviorSubject<object>(
    null
  );
  public commodityCategoryValueBasedOnDropDownValue =
    this.commodityCategoryValueBasedOnDropDown.asObservable();
  public installationCenterDetails = new BehaviorSubject<DeviceDataChart>(
    undefined
  );
  public installationCenterDetailsValue =
    this.commodityValueBasedOnDropDown.asObservable();
  public regionId = new BehaviorSubject<any>("");
  public regionIdValue = this.regionId.asObservable();
  public testingvalue: string;
  public instCenterId: string;
  public stateId: string;
  public commodityIdTobePassToAPI: string;
  public deviceType: string;
  public analyticBasedOnCommodity;
  public callAnalysisAPI = false;
  public raisePOCommodity: number;
  public raisePODeviceCount: number;
  public selectLeafType = 'Select Leaf Type';

  public token = {
    get() {
      const token = JSON.parse(localStorage.getItem("session"));
      console.log('🎫 [HelperService] token.get() called, has token:', !!token);
      return token;
    },
    set(token) {
      console.log('🎫 [HelperService] token.set() called with token (first 50 chars):', token ? token.substring(0, 50) + '...' : 'null');
      localStorage.setItem("session", JSON.stringify(token));
    },
    remove() {
      console.log('🎫 [HelperService] token.remove() called');
      localStorage.removeItem("session");
    },
  };

  public isLicenseExpired() {
    const userData =this.user.get();
    if(userData && userData.is_license_expired){
      return true;
    }
    return false;
  }
  public keycloakSession = {
    get() {
      return localStorage.getItem("isKeycloakLogin") === "true";
    },
    set(isKeycloak: boolean) {
      localStorage.setItem("isKeycloakLogin", String(isKeycloak));
    },
    remove() {
      localStorage.removeItem("isKeycloakLogin");
    },
  };

  public embeddedMode = {
    get() {
      return localStorage.getItem("isEmbeddedMode") === "true";
    },
    set(isEmbedded: boolean) {
      localStorage.setItem("isEmbeddedMode", String(isEmbedded));
    },
    remove() {
      localStorage.removeItem("isEmbeddedMode");
    },
  };

  public iframeBootstrap = {
    get() {
      return localStorage.getItem("isIframeBootstrap") === "true";
    },
    set(isIframeBootstrap: boolean) {
      localStorage.setItem("isIframeBootstrap", String(isIframeBootstrap));
    },
    remove() {
      localStorage.removeItem("isIframeBootstrap");
    },
  };

  public notifyParentAuthEvent(reason: string, extra: any = {}) {
    if (window.self === window.top || !this.embeddedMode.get()) {
      return;
    }

    const targetOrigin = environment.iframeParentOrigin || "*";
    window.parent.postMessage(
      {
        type: "QUALIX_IFRAME_AUTH_EXPIRED",
        reason,
        path: window.location.pathname,
        timestamp: Date.now(),
        ...extra,
      },
      targetOrigin
    );
  }

  public originalUrl = {
    get() {
      const url = localStorage.getItem("originalUrl");
      console.log('💾 [HelperService] originalUrl.get() called, returning:', url);
      return url;
    },
    set(url: string) {
      console.log('💾 [HelperService] originalUrl.set() called with:', url);
      localStorage.setItem("originalUrl", url);
      console.log('💾 [HelperService] originalUrl saved to localStorage');
    },
    remove() {
      console.log('💾 [HelperService] originalUrl.remove() called');
      const urlBeforeRemove = localStorage.getItem("originalUrl");
      console.log('💾 [HelperService] URL before removal:', urlBeforeRemove);
      localStorage.removeItem("originalUrl");
      console.log('💾 [HelperService] originalUrl removed from localStorage');
    },
  };

  public historytemplate = {
    get() {
      return localStorage.getItem("historytemplate");
    },
    set(templatename) {
      localStorage.setItem("historytemplate", templatename);
    },
    remove() {
      localStorage.removeItem("historytemplate");
    },
  };

  public adminPrimaryView = {
    get() {
      return localStorage.getItem("landingview");
    },
    set(landingview) {
      localStorage.setItem("landingview", landingview);
    },
    remove() {
      localStorage.removeItem("landingview");
    },
  };

  public adminiframe = {
    get() {
      return localStorage.getItem("iframeview");
    },
    set(iframecontent) {
      localStorage.setItem("iframeview", iframecontent);
    },
    remove() {
      localStorage.removeItem("iframeview");
    },
  };

  public scandetailtemplate = {
    get() {
      return localStorage.getItem("scandetailtemplate");
    },
    set(templatename) {
      localStorage.setItem("scandetailtemplate", templatename);
    },
    remove() {
      localStorage.removeItem("scandetailtemplate");
    },
  };

  public user = {
    get() {
      return JSON.parse(localStorage.getItem("user"));
    },
    set(user) {
      localStorage.setItem("user", JSON.stringify(user));
    },
    remove() {
      localStorage.removeItem("user");
    },
  };

  public activeCustomerId = {
    get(): number | null {
      const val = localStorage.getItem("activeCustomerId");
      return val ? Number(val) : null;
    },
    set(id: number) {
      if (id) {
        localStorage.setItem("activeCustomerId", id.toString());
      } else {
        localStorage.removeItem("activeCustomerId");
      }
    },
    remove() {
      localStorage.removeItem("activeCustomerId");
    },
  };

  public customers = {
    get(): any[] {
      try {
        const data = JSON.parse(localStorage.getItem("customers"));
        return Array.isArray(data) ? data : [];
      } catch (e) {
        localStorage.removeItem("customers");
        return [];
      }
    },
    set(list: any[]) {
      localStorage.setItem("customers", JSON.stringify(list || []));
    },
    remove() {
      localStorage.removeItem("customers");
    },
  };

  public permission = {
    get() {
      return JSON.parse(localStorage.getItem("permission"));
    },
    set(user) {
      localStorage.setItem("permission", JSON.stringify(user));
    },
    remove() {
      localStorage.removeItem("permission");
    },
  };

  public exportPermission = {
    get() {
      return JSON.parse(localStorage.getItem("exportPermission"));
    },
    set(user) {
      localStorage.setItem("exportPermission", JSON.stringify(user));
    },
    remove() {
      localStorage.removeItem("exportPermission");
    },
  };

  public templateConfig = {
    get(){
      return JSON.parse(localStorage.getItem("templateConfig"));
    },
    set(templateConfig) {
      localStorage.setItem("templateConfig", JSON.stringify(templateConfig));
    },
    remove(){
      localStorage.removeItem("templateConfig");
    }
  }

    public dashboardConfig = {
    get(){
      return JSON.parse(localStorage.getItem("dashboard_config"));
    },
    set(dashboardConfig) {
      localStorage.setItem("dashboard_config", JSON.stringify(dashboardConfig));
    },
    remove(){
      localStorage.removeItem("dashboard_config");
    }
  }

  public analaticCommodity = {
    get() {
      return localStorage.getItem("analyticCommodity");
    },
    set(commodityId) {
      localStorage.setItem("analyticCommodity", commodityId);
    },
    remove(commodityId) {
      localStorage.setItem("analyticCommodity", commodityId);
    },
  };

  public seasonsEnabled = {
    get() {
      return JSON.parse(localStorage.getItem("seasonsEnabled"));
    },
    set(value) {
      localStorage.setItem("seasonsEnabled", JSON.stringify(value));
    },
    remove() {
      localStorage.removeItem("seasonsEnabled");
    },
  }
  public hederaEnabled = {
    get() {
      return JSON.parse(localStorage.getItem("hederaEnabled"));
    },
    set(value) {
      localStorage.setItem("hederaEnabled", JSON.stringify(value));
    },
    remove() {
      localStorage.removeItem("hederaEnabled");
    },
  }
  
  public isScanLocationEnabled = {
    get() {
      return JSON.parse(localStorage.getItem("isScanLocationEnabled"));
    },
    set(value) {
      localStorage.setItem("isScanLocationEnabled", JSON.stringify(value));
    },
    remove() {
      localStorage.removeItem("isScanLocationEnabled");
    },
  }

  public productType = {
    get() {
      return localStorage.getItem("product_type");
    },
    set(value: string) {
      localStorage.setItem("product_type", value);
    },
    remove() {
      localStorage.removeItem("product_type");
    },
  }
  public isHederaEnabled(): boolean {
    if(this.hederaEnabled.get() != null && this.hederaEnabled.get() == true){
      return true;
    }

    return false;
  }

  public getProductType(): string {
    let pt = this.productType.get();
    
    if (typeof pt === 'string') {
      if (pt.length >= 2 && pt.startsWith('"') && pt.endsWith('"')) {
        pt = pt.substring(1, pt.length - 1);
      }
      pt = pt.trim(); 
    }
    
    return pt || 'assaying';
  }

  public Logout() {
    this.clearAllData();
    window.location.href = environment.iamhost + "logout?client_id=clientId";
  }

  public setRegionIdValue(value: any): void {
    this.regionId.next(value);
  }

  public setCommodityValue(value: SubscriptionBasedOnCategory[]): void {
    this.commoditySelected.next(value);
  }

  public setCommodityValueBasedOnDropDown(value: any): void {
    this.commodityValueBasedOnDropDown.next(value);
  }

  public setCommodityCategoryValueBasedOnDropDown(value: any): void {
    this.commodityCategoryValueBasedOnDropDown.next(value);
  }

  public setInstallationCenterDetailsValue(value: DeviceDataChart): void {
    if (value !== undefined) {
      this.getInstCenterId(value);
      this.installationCenterDetails.next(value);
    } else {
      this.getInstCenterId(undefined);
      this.installationCenterDetails.next(undefined);
    }
  }

  public setReactiveInstallationCenterDetailsValue(value: any): void {
    if (value !== undefined) {
      this.getReactiveInstCenterId(value);
      this.installationCenterDetails.next(value);
    } else {
      this.getReactiveInstCenterId(undefined);
      this.installationCenterDetails.next(undefined);
    }
  }

  public resetPaging() {
    this.pageSize = 10;
    this.skipPages = 0;
    this.listTotalRecords = 0;
  }

  public isPermitted(ispermit) {
    const permissions = this.permission.get();
    if (permissions.indexOf(ispermit) > -1) {
      return true;
    } else {
      return false;
    }
  }
  
  public getAuthInfo(): boolean {
    const token = this.token.get();
    const hasToken = token ? true : false;
    console.log('🔐 [HelperService] getAuthInfo() called - token exists:', hasToken);
    console.log('🔐 [HelperService] Token value (first 50 chars):', token ? token.substring(0, 50) + '...' : 'null');
    console.log('🔐 [HelperService] Keycloak session:', this.keycloakSession.get());
    return hasToken;
  }

  public formatDateForServer(value: Date): string {
    let d;
    let m;
    let y;
    d = ("0" + value.getDate()).slice(-2);
    m = ("0" + (value.getMonth() + 1)).slice(-2);
    y = value.getFullYear();
    return d + "/" + m + "/" + y;
  }

  public validateNumberInput(event) {
    const allowedRegex = /[0-9]/g;
    if (!event.key.match(allowedRegex)) {
      event.preventDefault();
    }
  }

  public validateEmail(email) {
    var regex =
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(email);
  }

  public browserBack() {
    window.history.back();
  }

  public urlFactory(key: APIURLTypes, urlPart: string) {
    return new APIUrlModel(key, urlPart);
  }

  /**
   * Stores the multi-organization fields of a login / keycloak-profile response.
   */
  public applyOrgMemberships(response: any): void {
    if (response?.customers && Array.isArray(response.customers)) {
      this.customers.set(response.customers);
    }
    if (response?.active_customer_id) {
      this.activeCustomerId.set(response.active_customer_id);
    } else if (response?.user?.customer_id) {
      this.activeCustomerId.set(response.user.customer_id);
    }
  }

  /**
   * The page a user starts on for the stored profile, matching the landing chosen after login
   * (KeycloakCallbackComponent). `/dashboard` alone has no page of its own.
   */
  public landingRoute(): string {
    const user = this.user.get();
    if (user?.customer_type === "SERVICE_PROVIDER") {
      return "/superadmin/customers";
    }
    if (this.adminPrimaryView.get() && (this.permission.get() || []).indexOf("shade_card_view") > -1) {
      return "/visio/shadecard";
    }
    return "/dashboard/landing";
  }

  /**
   * Removes every organization-scoped setting so values of a previously active
   * organization cannot leak into the next one (feature flags are only ever set when true).
   */
  public clearOrgScopedState(): void {
    this.permission.remove();
    this.exportPermission.remove();
    this.historytemplate.remove();
    this.adminPrimaryView.remove();
    this.adminiframe.remove();
    this.templateConfig.remove();
    this.seasonsEnabled.remove();
    this.hederaEnabled.remove();
    this.isScanLocationEnabled.remove();
    this.productType.remove();
    this.dashboardConfig.remove();
  }

  /**
   * Stores a keycloak-profile response (user, permissions, organization config and memberships).
   */
  public applyProfileResponse(response: any): void {
    if (response?.access_token) {
      this.token.set(response.access_token);
    }
    if (response?.user) {
      this.user.set(response.user);
    }
    if (response?.permissions) {
      this.permission.set(response.permissions);
    }
    if (response?.export_report) {
      this.exportPermission.set(response.export_report);
    }
    if (response?.scan_history_template) {
      this.historytemplate.set(response.scan_history_template);
    }
    if (response?.primary_view) {
      this.adminPrimaryView.set(response.primary_view);
    }
    if (response?.iframe_url) {
      this.adminiframe.set(response.iframe_url);
    }
    if (response?.template_config) {
      this.templateConfig.set(response.template_config);
    }
    if (response?.is_seasons_enabled) {
      this.seasonsEnabled.set(true);
    }
    if (response?.is_hedera_enabled) {
      this.hederaEnabled.set(true);
    }
    if (response?.scan_location_enabled) {
      this.isScanLocationEnabled.set(true);
    }
    if (response?.product_type) {
      this.productType.set(response.product_type);
    }
    if (response?.dashboard_config) {
      this.dashboardConfig.set(response.dashboard_config);
    }
    this.applyOrgMemberships(response);
  }

  public getCustomerId(): number {
    const activeId = this.activeCustomerId.get();
    if (activeId) {
      return Number(activeId);
    }
    const user = this.user.get();
    if (!user) {
      return null;
    }
    return user.customer_id ? Number(user.customer_id) : null;
  }
  public getUserId(): number {
    return this.user.get() && this.user.get().user_id;
  }
  public getUserEmail(): string {
    return this.user.get() && this.user.get().email;
  }

  public getInstCenterId(value: DeviceDataChart) {
    if (value !== undefined) {
      this.instCenterId = value.inst_center_id.toString();
      this.deviceType = "Visio"; // value.device_type_name.toString();
    } else {
      this.instCenterId = undefined;
      this.deviceType = undefined;
    }
    return value;
  }

  public getReactiveInstCenterId(value: any) {
    if (value !== undefined) {
      //this.instCenterId ='750';// value.locationId.toString();
      this.deviceType = "Visio"; // value.device_type_name.toString();
      //console.log({'value':value});
      this.instCenterId = value.locationId.toString();
      // this.deviceType = value.device_type_name.toString();
    } else {
      this.instCenterId = undefined;
      this.deviceType = undefined;
    }
    return value;
  }

  public setAnalyticBasedOnCommodity(value) {
    if (value.length > 0) {
      value = value.filter(({ analytic_name }) => analytic_name !== "Select");
    }
    this.analyticBasedOnCommodity = value;
  }

  public setAgnextAdmin(value) {
    this.isAdmin = value;
  }

  public IsAgnextAdmin() {
    const user = this.user.get();
    if (
      user &&
      user.customer_type &&
      user.customer_type === "SERVICE_PROVIDER"
    ) {
      return true;
    } else {
      return false;
    }
  }

  public IsOperations() {
    return this.getLoggedInUserRole().some((res) => res === "Operations");
  }

  public IsClient() {
    const user = this.user.get();
    if (user && user.customer_type && user.customer_type === "CLIENT") {
      return true;
    } else {
      return false;
    }
  }

  public IsCustomerAdmin() {
    const user = this.user.get();
    if (user && user.roles[0] === "customer_admin") {
      console.log(user);
      return true;
    } else return false;
  }

  public IsAgnextCustomerAdmin() {
    return this.getLoggedInUserRole().some((res) => res === "customer_admin");
    // const user = this.user.get();
    // if ( user && user.customer_type && user.customer_type === 'CUSTOMER') {
    //   return true;
    // } else {
    //   return false;
    // }
    // return this.isAdmin;
  }

  public IsStateAdmin() {
    return this.getLoggedInUserRole().some((res) => res === "state_admin");
  }

   public IsNafedStateAdmin () {
    const user = this.user.get();
    return user.customer_id == 183 &&  this.getLoggedInUserRole().some((res) => res === "state_admin") ;
  }

  public getStateAdminStateId(){
    // return this.user().some((res => res === 'state_admin'));
    const currentUserAddress = this.user.get().address;
    return currentUserAddress[0].state;
   }

  public getUserType(): string {
    const currentUser = this.user.get();
    if (currentUser && currentUser.customer_type) {
      return currentUser.customer_type;
    } else {
      return "";
    }
  }

  public getLoggedInUserRole(): Array<string> {
    const currentUser = this.user.get();
    if (currentUser && currentUser.roles) {
      return currentUser.roles;
    } else {
      return [];
    }
  }

  public getCalculatedDate(referenceDate: Date, difference: number): Date {
    return new Date(
      referenceDate.setDate(referenceDate.getDate() - difference)
    );
  }

  public getLastTimeOfTheDate(date) {
    return date.setHours(23, 59, 59, 999);
  }

  public markFormGroupTouched(formGroup: FormGroup) {
    (Object as any).values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      if (control.controls) {
        this.markFormGroupTouched(control);
      }
    });
  }

  public manageAnalyticFilterData(
    filters: AnalyticsFilter
  ): AnalyticFilterModel {
    const filter = new AnalyticFilterModel();
    filter.Commodity_id = filters.commodity__id ? filters.commodity__id : null;
    filter.Region_id = filters.region_id ? filters.region_id : null;
    filter.center_id = filters.installationcenter_id
      ? filters.installationcenter_id
      : null;
    filter.dateFrom = filters.dateFrom
      ? filters.dateFrom.getTime().toString()
      : null;
    filter.dateTo = filters.DateTo ? filters.DateTo.getTime().toString() : null;
    return filter;
  }

  public navigationRemovesubmenu(mainmenu, rootNodeTitle, removeItem) {
    let rootindex = mainmenu.findIndex((m) => m.title == rootNodeTitle);
    if (rootindex >= 0) {
      let rootNode = mainmenu[rootindex];
      //let modifiedSubMenu = this.helperService.manageNavigationsubmenu(this.menuItems,'Dashboard','Graphic View');
      if (rootNode.submenu && rootNode.submenu.length) {
        var index = rootNode.submenu.findIndex(
          (subItem) => subItem.title == removeItem
        );
        if (index !== -1) {
          rootNode.submenu.splice(index, 1);
          mainmenu[rootindex] = rootNode;
        }
      }
    }
    return mainmenu;
  }

  public navigationRemoveMainMenu(mainmenu, rootNodeTitle) {
    let rootindex = mainmenu.findIndex((m) => m.title == rootNodeTitle);
    if (rootindex >= 0) {
      mainmenu.splice(rootindex, 1);
    }
    return mainmenu;
  }

  public clearAllData() {
    localStorage.removeItem("session");
    localStorage.removeItem("user");
    localStorage.removeItem("permission");
    localStorage.removeItem("exportPermission");
    // localStorage.removeItem('landingview');
    localStorage.removeItem("iframeview");
    localStorage.removeItem("templateConfig");
    localStorage.removeItem("seasonsEnabled");
    localStorage.removeItem("hederaEnabled");
    localStorage.removeItem("isScanLocationEnabled");
    localStorage.removeItem("product_type");
    localStorage.removeItem("isKeycloakLogin");
    localStorage.removeItem("isEmbeddedMode");
    localStorage.removeItem("isIframeBootstrap");
    localStorage.removeItem("originalUrl");
    localStorage.removeItem("dashboard_config");
    localStorage.removeItem("activeCustomerId");
    localStorage.removeItem("customers");
  }
}

export function asPerFilterParams(
  filter: AnalyticsFilter,
  customerId?: number
): FilterParamsApiModel {
  return Object.assign(
    {},
    { commodity_id: filter.commodity__id ? filter.commodity__id : null },
    { region_id: filter.region_id ? filter.region_id : null },
    {
      inst_center_id: filter.installationcenter_id
        ? filter.installationcenter_id
        : null,
    },
    { date_to: filter.DateTo ? filter.DateTo.getTime() : null },
    { date_from: filter.dateFrom ? filter.dateFrom.getTime() : null }
  );
}
